// Shared sort comparators for AG Grid text columns (#10041).
//
// Natural sort for part-mark / voucher-number style values: trailing digits
// compare numerically (P2 < P10, 2 < 100). Mirrors main.gridsort.part_sort_keys
// so header-click sorting matches server-side sorted output (print preview).
// Referenced from Python colDefs as "window.naturalSortComparator".

import { parseDateCellValue } from './aggrid_dates.js';

// Complete numeric values (signed, decimal, comma-formatted) compare by
// value: the trailing-digit rule alone put "-2" before "-10" and "1.5"
// before "1.25". Blanks/text keep the prefix rule below.
const numericValueOrNull = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const s = value.replace(/,/g, '').trim();
  return /^[+-]?\d+(\.\d+)?$/.test(s) ? Number(s) : null;
};

export const naturalSortComparator = (a, b) => {
  // Each value classifies independently into one (prefix, number) key, so
  // the order is a single transitive total order even in mixed lists —
  // pairwise fast paths produced cycles like 1.5 < 2 < 1-2 < 1.5 (MR !17144
  // review). Key shapes mirror main.gridsort.sort_rows: blanks first
  // (-Infinity), complete numerics under the empty prefix by value, then
  // text grouped by prefix with the trailing number compared numerically.
  const key = (v) => {
    const num = numericValueOrNull(v);
    if (num !== null) return ['', num];
    const s = String(v ?? '').trim();
    if (!s) return ['', -Infinity];
    const m = s.match(/^(.*?)(\d+)[^0-9a-zA-Z]*$/);
    return m ? [m[1], parseInt(m[2], 10)] : [s, -1];
  };
  const [pa, na] = key(a);
  const [pb, nb] = key(b);
  if (pa !== pb) return pa < pb ? -1 : 1;
  if (na === nb) return 0;
  return na < nb ? -1 : 1;
};

// Set-filter value lists used AG Grid's plain string order, so "1, 10, 100, 2"
// and date lists grouped by first character. Dates compare chronologically,
// everything else naturally.
export const setFilterValueComparator = (a, b) => {
  const dateA = parseDateCellValue(a);
  const dateB = parseDateCellValue(b);
  if (dateA !== null && dateB !== null) return dateA.getTime() - dateB.getTime();
  // Fixed groups keep the order transitive in mixed lists: every parseable
  // date sorts before every non-date, then each group orders internally.
  if (dateA !== null) return -1;
  if (dateB !== null) return 1;
  return naturalSortComparator(a, b);
};
